import { SAREE_SORTS, STORE_NAME, type SareeFacets, type SareeSort } from '@neyara/shared';
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router';
import { ChevronIcon, CloseIcon, FilterIcon, SearchIcon } from '../components/icons';
import { Lotus, LotusDivider } from '../components/lotus';
import { SareeCard } from '../components/saree-card';
import { useFacets, useSarees } from '../lib/queries';
import { lockScroll, scrollToTarget, usePinOnDesktop } from '../lib/scroll';

const PAGE_SIZE = 24;
const SORT_LABELS: Record<SareeSort, string> = {
  newest: 'Newest first',
  'price-asc': 'Price: low to high',
  'price-desc': 'Price: high to low',
};
const FILTER_KEYS = ['q', 'category', 'occasion', 'fabric', 'color', 'minPrice', 'maxPrice', 'featured'] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

export function CatalogPage() {
  const [params, setParams] = useSearchParams();
  const { data: facets } = useFacets();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const get = (key: string) => params.get(key) ?? undefined;
  const sort = (SAREE_SORTS as readonly string[]).includes(params.get('sort') ?? '')
    ? (params.get('sort') as SareeSort)
    : 'newest';
  const page = Math.max(1, Number(params.get('page')) || 1);

  const query = {
    q: get('q'),
    category: get('category'),
    occasion: get('occasion'),
    fabric: get('fabric'),
    color: get('color'),
    minPrice: toNumber(get('minPrice')),
    maxPrice: toNumber(get('maxPrice')),
    featured: params.get('featured') === 'true' ? true : undefined,
    sort,
    page,
    pageSize: PAGE_SIZE,
  };
  const { data, isPending, isFetching, isError, refetch } = useSarees(query);
  // Previous results stay on screen while a new search or filter loads.
  const updating = isFetching && !isPending;
  const layout = useRef<HTMLDivElement>(null);
  const filtersPanel = useRef<HTMLDivElement>(null);
  usePinOnDesktop(filtersPanel, layout, [facets, data?.items.length, data?.page]);

  /** Sets (or clears) one param and resets pagination. */
  const update = (changes: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    if (!('page' in changes)) next.delete('page');
    setParams(next, { preventScrollReset: 'page' in changes ? false : true });
  };

  const category = facets?.categories.find((c) => c.slug === query.category);
  const active = FILTER_KEYS.filter((k) => params.get(k)).map((key) => ({
    key,
    label: chipLabel(key, params.get(key)!, facets),
  }));
  const activeCount = active.filter((a) => a.key !== 'q').length;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const title = category?.name ?? (query.featured ? 'Featured' : query.occasion ? `For ${query.occasion}` : 'The Sarees');

  return (
    <>
      <title>{`${title} - ${STORE_NAME}`}</title>
      <section className="tissue border-b border-champagne-deep">
        <div className="mx-auto max-w-7xl px-4 pt-14 pb-10 text-center sm:px-8 sm:pt-16">
          <h1 className="font-display text-[1.9rem] leading-tight font-medium tracking-[0.18em] text-maroon uppercase sm:text-[2.6rem]">
            {title}
          </h1>
          <LotusDivider className="mt-4" />
          {category?.description && (
            <p className="mx-auto mt-4 max-w-xl font-script text-xl text-ink-soft italic">{category.description}</p>
          )}
          <SearchField initial={query.q ?? ''} busy={updating} onSearch={(q) => update({ q })} />
        </div>
      </section>

      <div
        id="catalog-results"
        ref={layout}
        className="mx-auto max-w-7xl px-4 pt-8 pb-24 sm:px-8 lg:grid lg:grid-cols-[15rem_1fr] lg:items-start lg:gap-12"
      >
        <aside className="hidden lg:block" aria-label="Filters">
          <div ref={filtersPanel}>
            <Filters facets={facets} params={params} update={update} />
          </div>
        </aside>

        <div>
          <div className="relative flex flex-wrap items-center justify-between gap-3 border-b border-champagne-deep pb-4">
            <p className="tabular text-ink-soft" aria-live="polite">
              {updating ? 'Updating…' : data ? `${data.total} ${data.total === 1 ? 'saree' : 'sarees'}` : ' '}
            </p>
            {updating && (
              <span className="absolute inset-x-0 -bottom-px h-0.5 overflow-hidden" aria-hidden="true">
                <span className="block h-full w-1/3 animate-[results-load_1.1s_ease-in-out_infinite] bg-maroon" />
              </span>
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="btn btn-outline min-h-11 px-4 lg:hidden"
                aria-haspopup="dialog"
              >
                <FilterIcon size={18} /> Filters{activeCount > 0 && <span className="tabular">({activeCount})</span>}
              </button>
              <label className="sr-only" htmlFor="sort">
                Sort sarees
              </label>
              <select
                id="sort"
                value={sort}
                onChange={(e) => update({ sort: e.target.value === 'newest' ? undefined : e.target.value })}
                className="field min-h-11 w-auto cursor-pointer py-2 pr-9 text-sm"
              >
                {SAREE_SORTS.map((s) => (
                  <option key={s} value={s}>
                    {SORT_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {active.length > 0 && (
            <ul className="mt-4 flex flex-wrap items-center gap-2" aria-label="Active filters">
              {active.map((a) => (
                <li key={a.key}>
                  <button
                    type="button"
                    onClick={() => update({ [a.key]: undefined })}
                    className="inline-flex min-h-9 items-center gap-1.5 bg-maroon px-3 text-sm text-champagne transition-colors hover:bg-maroon-deep"
                    aria-label={`Remove filter ${a.label}`}
                  >
                    {a.label} <CloseIcon size={14} />
                  </button>
                </li>
              ))}
              <li>
                <Link to="/sarees" className="ml-1 text-sm text-maroon underline">
                  Clear all
                </Link>
              </li>
            </ul>
          )}

          <div
            className={`mt-10 transition-opacity duration-300 ${updating ? 'pointer-events-none opacity-40' : ''}`}
            aria-busy={updating}
          >
            {isError ? (
              <Message title="We couldn't load the sarees.">
                <button type="button" onClick={() => refetch()} className="btn btn-maroon mt-6">
                  Try again
                </button>
              </Message>
            ) : isPending ? (
              <div className="grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-8 xl:grid-cols-3" aria-busy="true">
                {Array.from({ length: 6 }, (_, i) => (
                  <div key={i} className="aspect-[3/4] animate-pulse bg-champagne/70" />
                ))}
              </div>
            ) : data.items.length === 0 ? (
              <Message title="No sarees match these filters.">
                <Link to="/sarees" className="btn btn-outline mt-6">
                  See all sarees
                </Link>
              </Message>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-8 xl:grid-cols-3">
                {data.items.map((saree, i) => (
                  <SareeCard key={saree.id} saree={saree} eager={i < 3} />
                ))}
              </div>
            )}
          </div>

          {data && totalPages > 1 && (
            <nav aria-label="Pages" className="mt-16 flex items-center justify-center gap-2">
              <PageLink disabled={page <= 1} onClick={() => update({ page: String(page - 1) })} label="Previous page">
                <ChevronIcon size={18} className="rotate-180" />
              </PageLink>
              <span className="tabular px-4 text-ink-soft">
                Page {page} of {totalPages}
              </span>
              <PageLink disabled={page >= totalPages} onClick={() => update({ page: String(page + 1) })} label="Next page">
                <ChevronIcon size={18} />
              </PageLink>
            </nav>
          )}
        </div>
      </div>

      {filtersOpen && (
        <FilterSheet onClose={() => setFiltersOpen(false)} total={data?.total} busy={updating}>
          <Filters facets={facets} params={params} update={update} />
        </FilterSheet>
      )}
    </>
  );
}

function toNumber(value: string | undefined) {
  const n = Number(value);
  return value && Number.isFinite(n) ? n : undefined;
}

function chipLabel(key: FilterKey, value: string, facets: SareeFacets | undefined) {
  switch (key) {
    case 'q':
      return `“${value}”`;
    case 'category':
      return facets?.categories.find((c) => c.slug === value)?.name ?? value;
    case 'minPrice':
      return `From ₹${Number(value).toLocaleString('en-IN')}`;
    case 'maxPrice':
      return `Up to ₹${Number(value).toLocaleString('en-IN')}`;
    case 'featured':
      return 'Featured';
    default:
      return value;
  }
}

function SearchField({
  initial,
  busy,
  onSearch,
}: {
  initial: string;
  busy: boolean;
  onSearch: (q: string | undefined) => void;
}) {
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSearch(value.trim() || undefined);
  };
  return (
    <form role="search" onSubmit={submit} className="mx-auto mt-8 flex max-w-lg items-center border-b border-maroon/40 focus-within:border-maroon">
      <label htmlFor="catalog-search" className="sr-only">
        Search sarees
      </label>
      <input
        id="catalog-search"
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Search by name, fabric or colour"
        className="min-h-12 w-full bg-transparent px-1 font-script text-xl text-charcoal italic placeholder:text-ink-soft/70 focus:outline-none"
      />
      <button type="submit" className="grid size-11 cursor-pointer place-items-center text-maroon" aria-label="Search">
        {busy ? (
          <span className="size-5 animate-spin rounded-full border-2 border-maroon/25 border-t-maroon" aria-hidden="true" />
        ) : (
          <SearchIcon />
        )}
      </button>
    </form>
  );
}

function Filters({
  facets,
  params,
  update,
}: {
  facets: SareeFacets | undefined;
  params: URLSearchParams;
  update: (changes: Record<string, string | undefined>) => void;
}) {
  if (!facets) return <div className="h-64 animate-pulse bg-champagne/50" aria-busy="true" />;
  const toggle = (key: string, value: string) => update({ [key]: params.get(key) === value ? undefined : value });

  return (
    <div className="space-y-8">
      {facets.categories.length > 0 && (
        <FilterGroup title="Weave">
          <ul className="space-y-0.5">
            {facets.categories.map((c) => {
              const on = params.get('category') === c.slug;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggle('category', c.slug)}
                    className={`flex min-h-10 w-full items-center gap-3 text-left transition-colors ${
                      on ? 'font-medium text-maroon' : 'text-charcoal hover:text-maroon'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`size-3 rotate-45 border ${on ? 'border-maroon bg-maroon' : 'border-gold-deep/60'}`}
                    />
                    {c.name}
                  </button>
                </li>
              );
            })}
          </ul>
        </FilterGroup>
      )}
      <ChipGroup title="Occasion" values={facets.occasions} paramKey="occasion" params={params} toggle={toggle} />
      <ChipGroup title="Fabric" values={facets.fabrics} paramKey="fabric" params={params} toggle={toggle} />
      <ChipGroup title="Colour" values={facets.colors} paramKey="color" params={params} toggle={toggle} />
      {facets.price.max > 0 && <PriceFilter facets={facets} params={params} update={update} />}
    </div>
  );
}

function FilterGroup({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id}>
      <h2 id={id} className="mb-3 font-display text-[0.8rem] font-medium tracking-[0.22em] text-maroon uppercase">
        {title}
      </h2>
      {children}
    </section>
  );
}

function ChipGroup({
  title,
  values,
  paramKey,
  params,
  toggle,
}: {
  title: string;
  values: string[];
  paramKey: string;
  params: URLSearchParams;
  toggle: (key: string, value: string) => void;
}) {
  if (values.length === 0) return null;
  return (
    <FilterGroup title={title}>
      <ul className="flex flex-wrap gap-2">
        {values.map((v) => {
          const on = params.get(paramKey) === v;
          return (
            <li key={v}>
              <button
                type="button"
                aria-pressed={on}
                onClick={() => toggle(paramKey, v)}
                className={`min-h-9 border px-3 text-sm transition-colors ${
                  on
                    ? 'border-maroon bg-maroon text-champagne'
                    : 'border-champagne-deep bg-white/50 text-charcoal hover:border-maroon'
                }`}
              >
                {v}
              </button>
            </li>
          );
        })}
      </ul>
    </FilterGroup>
  );
}

function PriceFilter({
  facets,
  params,
  update,
}: {
  facets: SareeFacets;
  params: URLSearchParams;
  update: (changes: Record<string, string | undefined>) => void;
}) {
  const [min, setMin] = useState(params.get('minPrice') ?? '');
  const [max, setMax] = useState(params.get('maxPrice') ?? '');
  useEffect(() => {
    setMin(params.get('minPrice') ?? '');
    setMax(params.get('maxPrice') ?? '');
  }, [params]);

  const apply = (e: FormEvent) => {
    e.preventDefault();
    update({ minPrice: min || undefined, maxPrice: max || undefined });
  };

  return (
    <FilterGroup title="Price (₹)">
      <form onSubmit={apply} className="flex items-end gap-2">
        <div className="flex-1">
          <label htmlFor="min-price" className="mb-1 block text-xs text-ink-soft">
            Min
          </label>
          <input
            id="min-price"
            inputMode="numeric"
            pattern="[0-9]*"
            value={min}
            onChange={(e) => setMin(e.target.value.replace(/\D/g, ''))}
            placeholder={String(facets.price.min)}
            className="field tabular px-2.5 py-2 text-sm"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="max-price" className="mb-1 block text-xs text-ink-soft">
            Max
          </label>
          <input
            id="max-price"
            inputMode="numeric"
            pattern="[0-9]*"
            value={max}
            onChange={(e) => setMax(e.target.value.replace(/\D/g, ''))}
            placeholder={String(facets.price.max)}
            className="field tabular px-2.5 py-2 text-sm"
          />
        </div>
        <button type="submit" className="btn btn-outline min-h-10 px-3 text-xs">
          Go
        </button>
      </form>
    </FilterGroup>
  );
}

function FilterSheet({
  children,
  onClose,
  total,
  busy,
}: {
  children: ReactNode;
  onClose: () => void;
  total?: number;
  busy: boolean;
}) {
  useEffect(() => {
    lockScroll(true);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      lockScroll(false);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Portaled out of the smooth-scroll content so fixed positioning isn't broken by its transforms.
  return createPortal(
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="absolute inset-0 animate-[fade-in_.3s_ease-out] bg-charcoal/45" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Filters"
        className="absolute inset-x-0 bottom-0 flex max-h-[85svh] animate-[sheet-in_.45s_var(--ease-out-expo)] flex-col bg-tissue"
      >
        <div className="flex items-center justify-between border-b border-champagne-deep px-5 py-3">
          <h2 className="font-display tracking-[0.22em] text-maroon uppercase">Filters</h2>
          <button type="button" onClick={onClose} className="-mr-2 grid size-11 place-items-center" aria-label="Close filters">
            <CloseIcon />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-6">{children}</div>
        <div className="border-t border-champagne-deep p-4">
          <button type="button" onClick={onClose} className="btn btn-maroon w-full">
            {busy ? 'Updating…' : `Show ${total ?? ''} ${total === 1 ? 'saree' : 'sarees'}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Message({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center py-16 text-center">
      <Lotus className="h-10 w-16 text-gold-deep" />
      <p className="mt-5 font-script text-3xl text-maroon italic">{title}</p>
      {children}
    </div>
  );
}

function PageLink({
  disabled,
  onClick,
  label,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        onClick();
        scrollToTarget('#catalog-results');
      }}
      disabled={disabled}
      aria-label={label}
      className="grid size-11 place-items-center border border-maroon/40 text-maroon transition-colors hover:bg-maroon hover:text-champagne disabled:pointer-events-none disabled:opacity-35"
    >
      {children}
    </button>
  );
}
